import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Configuration } from '../Configuration';
import { SurveyName, SurveyQuestionsResponse, SurveyResponse, SurveyType } from '@reviews/interfaces';
import { flowId, StorageKeys } from '@reviews/frontend';
import { GuestSurveyResponse } from '@reviews/interfaces';

export type ApiRequest = { pageNumber?: number; pageSize?: number; filters?: {} };

export type GuestContentUploadUrlItem = {
    s3_key: string;
    upload_url: string;
    expires_in: number;
};

export type GuestContentUploadUrlsRequest = {
    admin_reservation_id: number;
    nsu: string;
    confirmation?: string;
    count: number;
    mime_types: string[];
};

export const surveysApi = createApi({
    reducerPath: 'surveysApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${Configuration.getReviewsAPIUrl()}/v1`,
        prepareHeaders: (headers) => {
            headers.set('Content-Type', 'application/json');
            headers.set('Authorization', `Bearer ${localStorage.getItem(StorageKeys.ACCESS_TOKEN)}`);
            headers.set('x-identity-token', `${localStorage.getItem(StorageKeys.ID_TOKEN)}`);
            headers.set('X-Flow-Id', flowId);
            headers.set('X-Client-Identifier', 'reviews-guest-app');
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getSurveyForReservation: builder.query<
            { data: SurveyQuestionsResponse },
            {
                type: SurveyType;
                reservationId: number;
                confirmationCode: string;
                nsuHash: string;
                name: SurveyName;
                isPal?: boolean;
                withContent?: boolean;
            }
        >({
            query: (req) => {
                const params: { [key: string]: any } = {
                    'filter[reservation_id]': req.reservationId,
                    'filter[confirmation]': req.confirmationCode,
                    'filter[nsu]': req.nsuHash,
                    'filter[name]': req.name,
                };
                if (req.isPal) {
                    params['filter[pal]'] = 'true';
                }
                if (req.withContent) {
                    params['filter[with_content]'] = 'true';
                }
                return {
                    url: `/surveys/${req.type}`,
                    method: 'GET',
                    params,
                };
            },
        }),

        postSurveyResponse: builder.mutation<{ data: GuestSurveyResponse }, { surveyID: number; response: SurveyResponse }>({
            query: ({ surveyID, response }) => ({
                url: `/surveys/${surveyID}`,
                method: 'POST',
                body: {
                    data: {
                        type: 'survey-response',
                        attributes: response,
                    },
                },
            }),
        }),

        createGuestContentUploadUrls: builder.mutation<
            { data: { attributes: { uploads: GuestContentUploadUrlItem[] } } },
            GuestContentUploadUrlsRequest
        >({
            query: (body) => ({
                url: `/surveys/content/upload-urls`,
                method: 'POST',
                body: {
                    data: {
                        type: 'guest-content-upload-urls',
                        attributes: body,
                    },
                },
            }),
        }),
    }),
});

export const { useGetSurveyForReservationQuery, usePostSurveyResponseMutation, useCreateGuestContentUploadUrlsMutation } = surveysApi;
